import React from "react";

import {
  RadioGroup,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";

import SettingAlert from "@app/analytics/account/components/SettingAlert";
import SettingRadio from "@app/analytics/account/components/SettingRadio";
import SettingsTitle from "@app/analytics/account/components/SettingsTitle";
import {
  SettingSectionTitle,
  SwitchControlLabel,
} from "@app/analytics/account/WorkspaceSettings/components/styles";
import WorkspaceCanEditDropdown from "@app/analytics/account/WorkspaceSettings/components/WorkspaceCanEditDropdown";
import UpsellTooltipWrapper from "@app/analytics/components/UpsellTooltipWrapper";
import { PARTICIPANT_ACCESS_OPTIONS } from "@app/analytics/constants/sharing";
import useAuth from "@app/common/auth/useAuth";
import { TOOLTIPS } from "@app/common/content";
import * as UserUtils from "@app/common/utils/userUtils";
import type { EnterpriseWorkspaceSettings } from "@app/common/workspaces/types";

import { Bold } from "@rui/styles";
import Switch from "@rui/Switch";

const ReportSharingSettings = ({
  settings,
  onDropdownChange,
  onPreferenceChange,
  workspaceIsInactive,
}: {
  settings: EnterpriseWorkspaceSettings;
  onDropdownChange: (
    settingValue: boolean,
    settingName: keyof EnterpriseWorkspaceSettings
  ) => void;
  onPreferenceChange: (
    preferenceName: keyof EnterpriseWorkspaceSettings,
    type?: "toggle" | "value"
  ) => (event: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent) => void;
  workspaceIsInactive: boolean;
}) => {
  const { user } = useAuth();

  const userDomain = UserUtils.hasCompanyEmail(user)
    ? UserUtils.getEmailDomain(user)
    : null;

  return (
    <>
      <SettingSectionTitle>Report Sharing</SettingSectionTitle>
      <SettingAlert severity="info" persist show>
        <Typography variant="bodySecondaryReg">
          Read will use these settings to grant report access{" "}
          <Bold>when a Workspace member is the report owner</Bold>. Report
          owners will always automatically have report access.
        </Typography>
      </SettingAlert>
      <Stack spacing="12px">
        <Stack spacing="12px">
          <Stack spacing={1} padding="8px 0">
            <SettingsTitle
              title="Internal Participant Access"
              subtitle={`Control report access for internal ${
                userDomain ? `(@${userDomain})` : ""
              } meeting participants.`}
            />
            <UpsellTooltipWrapper show={workspaceIsInactive} placement="right">
              <RadioGroup
                onChange={onPreferenceChange(
                  "internalParticipantAccessLevelDefault"
                )}
                value={settings.internalParticipantAccessLevelDefault}
                sx={{ width: "fit-content" }}
              >
                {PARTICIPANT_ACCESS_OPTIONS.internal.map((option) => (
                  <SettingRadio
                    key={`internal-${option.value}`}
                    value={option.value}
                    disabled={workspaceIsInactive}
                    checked={
                      option.value ===
                      settings.internalParticipantAccessLevelDefault
                    }
                    label={option.label}
                    recommended={option.default}
                    description={option.description}
                  />
                ))}
              </RadioGroup>
            </UpsellTooltipWrapper>
            <WorkspaceCanEditDropdown
              disabled={workspaceIsInactive}
              onUpdate={onDropdownChange}
              name="internalParticipantAccessLevelDefaultIsUserEditable"
              selectedValue={
                settings.internalParticipantAccessLevelDefaultIsUserEditable
              }
              tooltip={
                workspaceIsInactive ? TOOLTIPS.upsell.upgradeToPro : undefined
              }
            />
          </Stack>
        </Stack>
        <Stack spacing="12px">
          <Stack spacing={1} padding="8px 0">
            <SettingsTitle
              title="External Participant Access"
              subtitle={`Control report access for external ${
                userDomain ? `(not @${userDomain})` : ""
              } meeting participants.`}
            />
            <UpsellTooltipWrapper show={workspaceIsInactive} placement="right">
              <RadioGroup
                onChange={onPreferenceChange(
                  "externalParticipantAccessLevelDefault"
                )}
                value={settings.externalParticipantAccessLevelDefault}
                sx={{ width: "fit-content" }}
              >
                {PARTICIPANT_ACCESS_OPTIONS.external.map((option) => (
                  <SettingRadio
                    key={`internal-${option.value}`}
                    value={option.value}
                    checked={
                      option.value ===
                      settings.externalParticipantAccessLevelDefault
                    }
                    disabled={workspaceIsInactive}
                    label={option.label}
                    recommended={option.default}
                    description={option.description}
                  />
                ))}
              </RadioGroup>
            </UpsellTooltipWrapper>
            <WorkspaceCanEditDropdown
              disabled={workspaceIsInactive}
              onUpdate={onDropdownChange}
              name="externalParticipantAccessLevelDefaultIsUserEditable"
              selectedValue={
                settings.externalParticipantAccessLevelDefaultIsUserEditable
              }
              tooltip={
                workspaceIsInactive ? TOOLTIPS.upsell.upgradeToPro : undefined
              }
            />
          </Stack>
          <UpsellTooltipWrapper show={workspaceIsInactive} placement="right">
            <SwitchControlLabel
              disabled={workspaceIsInactive}
              sx={{ alignItems: "flex-start" }}
              control={
                <Switch
                  checked={settings.anyoneWithLinkAccess}
                  onChange={onPreferenceChange(
                    "anyoneWithLinkAccess",
                    "toggle"
                  )}
                />
              }
              label={
                <SettingsTitle
                  title="Allow public link access"
                  subtitle='Workspace members can set report access to "anyone with the link".'
                  // TODO: ENG-11271 use the text below
                  // subtitle='Workspace members can set report access to "anyone with the link", and share video recaps using publicly accessible links.'
                />
              }
            />
          </UpsellTooltipWrapper>
        </Stack>
      </Stack>
    </>
  );
};

export default ReportSharingSettings;
